import { useApolloClient } from '@apollo/client'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import addDays from 'date-fns/addDays'
import isBefore from 'date-fns/isBefore'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { CloseIcon } from '@10xLabs/react-web/components/Icons/Close'
import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { Row } from '@10xLabs/react-web/components/Row'
import { Select } from 'components/v2/Select'
import { SelectTags } from 'components/v2/SelectTags'
import { InputCurrency } from 'components/InputCurrency'
import { InputDateTime } from 'components/InputDateTime'
import FormLayout from 'features/App/components/FormLayout'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { DOW } from 'features/FareFamilies/components/Dow/constants'
import { dowMessages } from 'features/FareFamilies/components/Dow/messages'
import { resolvers as loginResolvers } from 'resolvers/login'
import { getUnprocessableErrors } from 'utils/error'
import { safeFormatMessage } from 'utils/intl'
import { sanitizePrice } from 'utils/math'
import { device } from 'utils/style'
import { OneWay, BothWay, ToggleIsReturn, SelectSegmentsForm } from 'components/SelectSegments'
import { SelectRouteServices, getValidRouteServicesBySegments } from './SelectRouteServices'
import { schema } from './schema'
import { useAllSegmentsByCities } from 'features/Segments/hooks'
import { mapSegmentSuggestionsToFormSegments } from 'features/Segments/utils'
import { IFormSegment } from 'components/SelectSegments/types'

const DOW_LIST = [DOW.MONDAY, DOW.TUESDAY, DOW.WEDNESDAY, DOW.THURSDAY, DOW.FRIDAY, DOW.SATURDAY, DOW.SUNDAY]

const EXCEPTION_TYPE = {
  DOW: 'dow'
}

const EXCEPTION_TYPE_LIST = [EXCEPTION_TYPE.DOW]

const IntervalDelete = styled.div<TSFixMe>`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translate(100%, -50%);
`

const NestedRow = styled(Row)<TSFixMe>`
  display: flex;
  flex-direction: column;

  @media ${device.laptop} {
    flex-direction: row;
  }
`

const SpaceBetween = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Relative = styled.div<TSFixMe>`
  position: relative;
`

const SelectTagsWrapper = styled.div<TSFixMe>`
  margin-top: -5px;
`

const FlexEndColumn = styled(Column)<TSFixMe>`
  align-self: flex-end;
  position: relative;
`

export const SplitColumn = styled(Column)<TSFixMe>`
  flex: 0.1;
  align-self: center;
  text-align: center;
`

const StyledCloseIcon = styled(CloseIcon)<TSFixMe>`
  cursor: pointer;
`

const messages = defineMessages({
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  namePlaceholder: {
    id: 'dictionary.name',
    defaultMessage: 'Name'
  },
  descriptionPlaceholder: {
    id: 'dictionary.description',
    defaultMessage: 'Description'
  },
  startAtPlaceholder: {
    id: 'fareRules.form.startAt.placehodlder',
    defaultMessage: 'Starts on'
  },
  endAtPlaceholder: {
    id: 'fareRules.form.endAt.placehodlder',
    defaultMessage: 'Ends on'
  },
  exceptionTypePlaceholder: {
    id: 'fareRules.form.exception.type.placehodlder',
    defaultMessage: 'Select exception type'
  },
  selectDow: {
    id: 'fareRules.form.exception.selectDow',
    defaultMessage: 'Select day of week'
  },
  originPlaceholder: {
    id: 'fareRules.form.origin.placeholder',
    defaultMessage: 'Select an origin'
  },
  destinationPlaceholder: {
    id: 'fareRules.form.destination.placeholder',
    defaultMessage: 'Select a destination'
  }
})

export const getFilteredTags = (values: TSFixMe, list: TSFixMe): TSFixMe => {
  return list.filter((value: TSFixMe): TSFixMe => {
    return values.indexOf(value) === -1
  })
}

const resetSegmentsAndServices = ({ setFieldValue }: TSFixMe): TSFixMe => {
  resetSegments({ setFieldValue })
  resetServices({ setFieldValue })
}

const resetSegments = ({ setFieldValue }: TSFixMe): TSFixMe => {
  setFieldValue('allSegmentsAreSelected', false)
  setFieldValue('segments', [])
  setFieldValue('segmentsOrigins', [])
  setFieldValue('segmentsDestinations', [])
}

const resetServices = ({ setFieldValue }: TSFixMe): TSFixMe => {
  setFieldValue('allServicesAreSelected', false)
  setFieldValue('routeServices', [])
}

const NewFareRuleForm = (props: TSFixMe): TSFixMe => {
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    handleSubmit,
    error,
    loading,
    showCompany,
    isEdit
  } = props

  const intl = useIntl()

  const { segmentSuggestions } = useAllSegmentsByCities({
    companyId: values.company?.id,
    cityOrigins: [values.origin],
    cityDestinations: [values.destination],
    isBothWays: values.isReturn
  })
  const segmentsOptions = mapSegmentSuggestionsToFormSegments(segmentSuggestions) as IFormSegment[]

  const handleCompanyChange = (value: TSFixMe): TSFixMe => {
    setFieldValue('company', value)
    resetSegmentsAndServices({ setFieldValue })
  }

  const addInterval = (key: TSFixMe): TSFixMe => {
    setFieldValue(key, [
      ...values[key],
      {
        startAt: '',
        endAt: ''
      }
    ])
  }

  const addException = (): TSFixMe => {
    setFieldValue('exceptions', [
      ...values.exceptions,
      {
        key: '',
        value: ''
      }
    ])
  }

  const deleteInterval = (key: TSFixMe, index: TSFixMe): TSFixMe => {
    setFieldValue(
      key,
      values[key].filter((_: TSFixMe, intervalIndex: TSFixMe): TSFixMe => intervalIndex !== index)
    )
  }

  const deleteException = (index: TSFixMe): TSFixMe => {
    setFieldValue(
      'exceptions',
      values.exceptions.filter((_: TSFixMe, intervalIndex: TSFixMe): TSFixMe => intervalIndex !== index)
    )
  }

  const handleDowChange = (dow: TSFixMe): TSFixMe => {
    setFieldValue(
      'exceptions',
      values.exceptions.map((exception: TSFixMe): TSFixMe => {
        if (exception.key === EXCEPTION_TYPE.DOW) {
          return {
            ...exception,
            value: dow
          }
        }
        return exception
      })
    )
  }

  const handleOriginChange = (value: TSFixMe): TSFixMe => {
    handleCityChange('origin', value)

    if (values.destination.id === value.id) {
      setFieldValue('isReturn', false)
    }
  }

  const handleDestinationChange = (value: TSFixMe): TSFixMe => {
    handleCityChange('destination', value)

    if (values.origin.id === value.id) {
      setFieldValue('isReturn', false)
    }
  }

  const handleCityChange = (key: TSFixMe, value: TSFixMe): TSFixMe => {
    setFieldValue(key, value)
    setFieldTouched(key, true)

    resetSegmentsAndServices({ setFieldValue })
  }

  const canAddException = EXCEPTION_TYPE_LIST.length > values.exceptions.length

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="dictionary.fareRule" defaultMessage="Fare rule" />
            </Heading.H5>
          </Column>
        </Row>
        {showCompany && (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company.id"
                onChange={handleCompanyChange}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="description"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.description}
              placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
              label={<FormattedMessage id="fareRules.new.description.label" defaultMessage="Description (optional)" />}
              touched={touched.description}
              errors={errors.description}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="fareRules.new.selectServices.title" defaultMessage="Select services" />
            </Heading.H5>
          </Column>
        </Row>
        <div>
          <Label>
            <FormattedMessage id="fareRules.new.cities.label" defaultMessage="Route" />
          </Label>
        </div>
        <Row>
          <Column>
            <CityAutocomplete
              id="origin.id"
              value={values.origin.name}
              onChange={handleOriginChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.originPlaceholder)}
              touched={touched.origin && touched.origin.id}
              errors={errors.origin && errors.origin.id}
            />
          </Column>
          <SplitColumn>
            <ToggleIsReturn setFieldValue={setFieldValue} values={values}>
              {values.isReturn ? <BothWay /> : <OneWay />}
            </ToggleIsReturn>
          </SplitColumn>
          <Column>
            <CityAutocomplete
              id="destination.id"
              value={values.destination.name}
              onChange={handleDestinationChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.destinationPlaceholder)}
              touched={touched.destination && touched.destination.id}
              errors={errors.destination && errors.destination.id}
            />
          </Column>
        </Row>
        <div>
          <Label>
            <FormattedMessage id="fareRules.new.segments.label" defaultMessage="Segments" />
          </Label>
        </div>
        <SelectSegmentsForm
          segments={segmentsOptions}
          values={values}
          setFieldValue={setFieldValue}
          isReturn={values.isReturn}
          segmentsOrigins={values.segmentsOrigins}
          segmentsDestinations={values.segmentsDestinations}
          setSegmentsOrigins={(segmentsOrigins): TSFixMe => setFieldValue('segmentsOrigins', segmentsOrigins)}
          setSegmentsDestinations={(segmentsDestinations: TSFixMe): TSFixMe =>
            setFieldValue('segmentsDestinations', segmentsDestinations)
          }
          setSegments={(segments): TSFixMe => {
            setFieldValue('segments', segments)
            setFieldValue('allServicesAreSelected', false)
            setFieldValue(
              'routeServices',
              getValidRouteServicesBySegments({
                segments,
                routeServices: values.routeServices
              })
            )
          }}
          allSegmentsAreSelected={values.allSegmentsAreSelected}
          setAllSegmentsAreSelected={(allSegmentsAreSelected): TSFixMe => {
            setFieldValue('allSegmentsAreSelected', allSegmentsAreSelected)
          }}
          isShowingAllSegmentsCheckbox
          disabled={values.allSegmentsAreSelected}
          touched={touched}
          errors={errors}
          companyId={values.company.id || undefined}
        />
        <div>
          <Label>
            <FormattedMessage id="fareRules.new.routeServices.label" defaultMessage="Services" />
          </Label>
        </div>
        <SelectRouteServices
          values={values.routeServices}
          setValues={(routeServices: TSFixMe): TSFixMe => setFieldValue('routeServices', routeServices)}
          segments={values.segments}
          cityOrigin={values.origin}
          cityDestination={values.destination}
          isReturn={values.isReturn}
          touched={touched && touched.routeServices}
          errors={errors && errors.routeServices}
          isShowingAllServicesCheckbox
          allServicesAreSelected={values.allServicesAreSelected}
          setAllServicesAreSelected={(allServicesAreSelected: TSFixMe): TSFixMe => {
            setFieldValue('allServicesAreSelected', allServicesAreSelected)
          }}
        />
        <Row>
          <Column>
            <SpaceBetween>
              <Heading.H5>
                <FormattedMessage id="fareRules.form.departureAtRanges.label" defaultMessage="Travel Date" />
              </Heading.H5>
              <Button kind="secondary" type="button" onClick={(): TSFixMe => addInterval('departureAtRanges')}>
                <FormattedMessage id="dictionary.add" defaultMessage="Add" />
              </Button>
            </SpaceBetween>
          </Column>
        </Row>
        {values.departureAtRanges.map(({ startAt, endAt }: TSFixMe, index: TSFixMe): TSFixMe => {
          return (
            <Row key={index}>
              <Column>
                <NestedRow>
                  <Column>
                    <InputDateTime
                      id={`departureAtRanges[${index}].startAt`}
                      intl={intl}
                      value={startAt}
                      onChange={(value): TSFixMe => {
                        setFieldValue(`departureAtRanges[${index}].startAt`, value)
                        /*@ts-expect-error ts(2322) */

                        if (!endAt || isBefore(endAt, value)) {
                          /*@ts-expect-error ts(2322) FIXME*/
                          setFieldValue(`departureAtRanges[${index}].endAt`, endOfDay(value))
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={
                        touched.departureAtRanges &&
                        touched.departureAtRanges[index] &&
                        touched.departureAtRanges[index].startAt
                      }
                      errors={
                        errors.departureAtRanges &&
                        errors.departureAtRanges[index] &&
                        errors.departureAtRanges[index].startAt
                      }
                      placeholder={intl.formatMessage(messages.startAtPlaceholder)}
                      label={
                        <FormattedMessage
                          id="fareRules.form.interval.label"
                          defaultMessage="Interval {count}"
                          values={{
                            count: index + 1
                          }}
                        />
                      }
                    />
                  </Column>
                  <FlexEndColumn>
                    <InputDateTime
                      id={`departureAtRanges[${index}].endAt`}
                      intl={intl}
                      value={endAt}
                      onChange={(value): TSFixMe => {
                        setFieldValue(`departureAtRanges[${index}].endAt`, value)
                        /*@ts-expect-error ts(2322) */

                        if (!startAt || isBefore(value, startAt)) {
                          /*@ts-expect-error ts(2322) */

                          setFieldValue(`departureAtRanges[${index}].startAt`, startOfDay(value))
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={
                        touched.departureAtRanges &&
                        touched.departureAtRanges[index] &&
                        touched.departureAtRanges[index].endAt
                      }
                      errors={
                        errors.departureAtRanges &&
                        errors.departureAtRanges[index] &&
                        errors.departureAtRanges[index].endAt
                      }
                      placeholder={intl.formatMessage(messages.endAtPlaceholder)}
                      label=""
                    />
                    {values.departureAtRanges.length > 1 && (
                      <IntervalDelete>
                        <div onClick={(): TSFixMe => deleteInterval('departureAtRanges', index)}>
                          <StyledCloseIcon />
                        </div>
                      </IntervalDelete>
                    )}
                  </FlexEndColumn>
                </NestedRow>
              </Column>
            </Row>
          )
        })}
        <Row>
          <Column>
            <SpaceBetween>
              <Heading.H5>
                <FormattedMessage
                  id="fareRules.form.activationAtRanges.label"
                  defaultMessage="Active Periods (optional)"
                />
              </Heading.H5>
              {values.activationAtRanges.length > 0 && (
                <Button kind="secondary" type="button" onClick={(): TSFixMe => addInterval('activationAtRanges')}>
                  <FormattedMessage id="dictionary.add" defaultMessage="Add" />
                </Button>
              )}
            </SpaceBetween>
          </Column>
        </Row>
        {values.activationAtRanges.length === 0 && (
          <Row>
            <Column>
              <Button kind="secondary" type="button" onClick={(): TSFixMe => addInterval('activationAtRanges')}>
                <FormattedMessage id="fareRules.form.activationAtRanges.add" defaultMessage="Add new interval" />
              </Button>
            </Column>
          </Row>
        )}
        {values.activationAtRanges.map(({ startAt, endAt }: TSFixMe, index: TSFixMe): TSFixMe => {
          return (
            <Row key={index}>
              <Column>
                <NestedRow>
                  <Column>
                    <InputDateTime
                      id={`activationAtRanges[${index}].startAt`}
                      intl={intl}
                      value={startAt}
                      onChange={(value): TSFixMe => {
                        setFieldValue(`activationAtRanges[${index}].startAt`, value)
                        /*@ts-expect-error ts(2322) */

                        if (!endAt || isBefore(endAt, value)) {
                          /*@ts-expect-error ts(2322) FIXME*/
                          setFieldValue(`activationAtRanges[${index}].endAt`, addDays(startOfDay(value), 1))
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      intervalMinutes={15}
                      touched={
                        touched.activationAtRanges &&
                        touched.activationAtRanges[index] &&
                        touched.activationAtRanges[index].startAt
                      }
                      errors={
                        errors.activationAtRanges &&
                        errors.activationAtRanges[index] &&
                        errors.activationAtRanges[index].startAt
                      }
                      placeholder={intl.formatMessage(messages.startAtPlaceholder)}
                      label={
                        <FormattedMessage
                          id="fareRules.form.interval.label"
                          defaultMessage="Interval {count}"
                          values={{
                            count: index + 1
                          }}
                        />
                      }
                    />
                  </Column>
                  <FlexEndColumn>
                    <InputDateTime
                      id={`activationAtRanges[${index}].endAt`}
                      intl={intl}
                      value={endAt}
                      onChange={(value): TSFixMe => {
                        setFieldValue(`activationAtRanges[${index}].endAt`, value)

                        /*@ts-expect-error ts(2322) FIXME*/
                        if (!startAt || isBefore(value, startAt)) {
                          /*@ts-expect-error ts(2322) FIXME*/
                          setFieldValue(`activationAtRanges[${index}].startAt`, startOfDay(value))
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      intervalMinutes={15}
                      touched={
                        touched.activationAtRanges &&
                        touched.activationAtRanges[index] &&
                        touched.activationAtRanges[index].endAt
                      }
                      errors={
                        errors.activationAtRanges &&
                        errors.activationAtRanges[index] &&
                        errors.activationAtRanges[index].endAt
                      }
                      placeholder={intl.formatMessage(messages.endAtPlaceholder)}
                      label=""
                    />
                    <IntervalDelete>
                      <div onClick={(): TSFixMe => deleteInterval('activationAtRanges', index)}>
                        <StyledCloseIcon />
                      </div>
                    </IntervalDelete>
                  </FlexEndColumn>
                </NestedRow>
              </Column>
            </Row>
          )
        })}
        <Row>
          <Column>
            <SpaceBetween>
              <Heading.H5>
                <FormattedMessage id="fareRules.form.exceptions.label" defaultMessage="Exceptions" />
              </Heading.H5>
              {values.exceptions.length > 0 && canAddException && (
                <Button kind="secondary" type="button" onClick={(): TSFixMe => addException()}>
                  <FormattedMessage id="dictionary.add" defaultMessage="Add" />
                </Button>
              )}
            </SpaceBetween>
          </Column>
        </Row>
        {values.exceptions.length === 0 && (
          <Row>
            <Column>
              <Button kind="secondary" type="button" onClick={(): TSFixMe => addException()}>
                <FormattedMessage id="fareRules.form.exceptions.add" defaultMessage="Add new exception" />
              </Button>
            </Column>
          </Row>
        )}
        {values.exceptions.map((exception: TSFixMe, index: TSFixMe): TSFixMe => {
          return (
            <Row key={index}>
              <Column width="50%">
                <Label>
                  <FormattedMessage id="fareRules.form.exceptions.type.label" defaultMessage="Exception type" />
                </Label>
                <Relative>
                  <Select
                    id={`exceptions[${index}].key`}
                    value={values.exceptions[index].key}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">{intl.formatMessage(messages.exceptionTypePlaceholder)}</option>
                    {EXCEPTION_TYPE_LIST.map((exceptionType): TSFixMe => {
                      return (
                        <option key={exceptionType} value={exceptionType}>
                          {exceptionType}
                        </option>
                      )
                    })}
                  </Select>
                  {!exception.key && (
                    <IntervalDelete>
                      <div onClick={(): TSFixMe => deleteException(index)}>
                        <StyledCloseIcon />
                      </div>
                    </IntervalDelete>
                  )}
                </Relative>
              </Column>
              <Column width="50%">
                {exception.key && exception.key === EXCEPTION_TYPE.DOW && (
                  <>
                    <Label>
                      <FormattedMessage id="dictionary.dow" defaultMessage="Dow" />
                    </Label>
                    <Relative>
                      <SelectTagsWrapper>
                        <SelectTags
                          id="dow"
                          placeholder={intl.formatMessage(messages.selectDow)}
                          values={exception.value || []}
                          items={getFilteredTags(exception.value, DOW_LIST)}
                          getTagLabel={(value: TSFixMe): TSFixMe =>
                            safeFormatMessage({ intl, messages: dowMessages, key: value })
                          }
                          onChange={handleDowChange}
                        />
                      </SelectTagsWrapper>
                      <IntervalDelete>
                        <div onClick={(): TSFixMe => deleteException(index)}>
                          <StyledCloseIcon />
                        </div>
                      </IntervalDelete>
                    </Relative>
                  </>
                )}
              </Column>
            </Row>
          )
        })}
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="fareRules.form.fare.label" defaultMessage="Fare" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputCurrency
              id="price"
              value={sanitizePrice(values.price)}
              placeholder="$0.00"
              label={<FormattedMessage id="dictionary.price" defaultMessage="Price" />}
              onChange={(value: TSFixMe): TSFixMe => {
                setFieldValue('price', value)
              }}
              onBlur={handleBlur}
              touched={touched.price}
              errors={errors.price}
              hideSuffix
            />
          </Column>
        </Row>
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="fareRules.form.error"
                defaultMessage="There was an error while creating your fare rule"
              />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/fare-rules" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewFareRule = ({ fareRule = {}, showCompany, onSubmit, error, loading, isEdit }: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const tomorrow = addDays(new Date(), 1)

  const initialFareRule = {
    company: fareRule.company || { id: '', name: '' },
    name: fareRule.name || '',
    description: fareRule.description || '',
    price: fareRule.price || '',
    activationAtRanges: fareRule.activationAtRanges
      ? fareRule.activationAtRanges.map(({ startAt, endAt }: TSFixMe): TSFixMe => {
          return {
            startAt: new Date(startAt),
            endAt: new Date(endAt)
          }
        })
      : [],
    departureAtRanges: fareRule.departureAtRanges
      ? fareRule.departureAtRanges.map(({ startAt, endAt }: TSFixMe): TSFixMe => {
          return {
            startAt: new Date(startAt),
            endAt: new Date(endAt)
          }
        })
      : [
          {
            startAt: startOfDay(tomorrow),
            endAt: endOfDay(tomorrow)
          }
        ],
    exceptions: fareRule.exceptions || [],
    origin: fareRule.origin || {
      id: '',
      name: ''
    },
    destination: fareRule.destination || {
      id: '',
      name: ''
    },
    segments: fareRule.segments || [],
    segmentsOrigins: fareRule.segmentsOrigins || [],
    segmentsDestinations: fareRule.segmentsDestinations || [],
    allSegmentsAreSelected: fareRule?.allSegmentsAreSelected,
    routeServices: fareRule.routeServices || [],
    allServicesAreSelected: fareRule?.allServicesAreSelected,
    isReturn: fareRule.isReturn === undefined ? true : fareRule.isReturn
  }

  return (
    <Formik initialValues={initialFareRule} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return <NewFareRuleForm {...props} isEdit={isEdit} showCompany={showCompany} error={error} loading={loading} />
      }}
    </Formik>
  )
}
